/**
 * WHYSHIFT product hero — selling-plan toggle, variant picker layout, variant media sync.
 * Delivery + price updates are handled by native theme blocks.
 */
class WhyshiftProductHero extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #variantPickerObserver;

  /** @type {HTMLElement | null} */
  #sectionElement = null;

  /** @type {ResizeObserver | undefined} */
  #mobileGalleryObserver;

  /** @type {number[]} */
  #repairTimeouts = [];

  /** @type {boolean} */
  #lifecycleBound = false;

  /** @type {boolean} */
  #mobilePaginationBound = false;

  /** @type {number | undefined} */
  #mobilePaginationFrame;

  #isMobileViewport() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  #isMobileGalleryVisible() {
    if (!this.#isMobileViewport()) return false;

    const gallery = this.querySelector('.whyshift-product-hero__mobile-gallery');
    return gallery instanceof HTMLElement && gallery.offsetWidth > 0;
  }

  #needsMobileGalleryRepair(img) {
    if (!(img instanceof HTMLImageElement)) return false;
    if (!this.#isMobileGalleryVisible()) return false;

    const rect = img.getBoundingClientRect();
    return !img.complete || img.naturalWidth === 0 || rect.width === 0 || rect.height === 0;
  }

  #repairImage(img) {
    if (!(img instanceof HTMLImageElement)) return;

    const src = img.currentSrc || img.src;
    if (!src) return;

    img.loading = 'eager';

    const retrySrc = () => {
      if (this.#needsMobileGalleryRepair(img)) {
        img.src = src;
      }
    };

    if (img.decode) {
      img.decode().then(retrySrc).catch(retrySrc);
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(retrySrc);
    });
  }

  #ensureMobileGalleryImages() {
    if (!this.#isMobileViewport()) return;

    this.querySelectorAll('.whyshift-product-hero__mobile-slide img').forEach((img) => {
      if (!this.#needsMobileGalleryRepair(img)) return;
      this.#repairImage(img);
    });
  }

  #scheduleRepairPasses() {
    this.#clearRepairTimeouts();

    [0, 300, 1500].forEach((delay) => {
      const timeoutId = window.setTimeout(() => this.#ensureMobileGalleryImages(), delay);
      this.#repairTimeouts.push(timeoutId);
    });
  }

  #clearRepairTimeouts() {
    this.#repairTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    this.#repairTimeouts = [];
  }

  #bindLifecycleEvents() {
    if (this.#lifecycleBound) return;

    window.addEventListener('pageshow', this.#onPageShow);
    window.addEventListener('pagereveal', this.#onPageReveal);
    this.#lifecycleBound = true;
  }

  #onPageShow = () => {
    this.#scheduleRepairPasses();
  };

  #onPageReveal = () => {
    this.#scheduleRepairPasses();
  };

  #observeMobileGallery() {
    const viewport = this.querySelector('.whyshift-product-hero__mobile-viewport');
    if (!(viewport instanceof HTMLElement) || this.#mobileGalleryObserver) return;

    this.#mobileGalleryObserver = new ResizeObserver(() => {
      this.#ensureMobileGalleryImages();
    });

    this.#mobileGalleryObserver.observe(viewport);
  }

  #bindMobileImageEvents() {
    this.querySelectorAll('.whyshift-product-hero__mobile-slide img').forEach((img) => {
      if (!(img instanceof HTMLImageElement) || img.dataset.whyshiftHeroBound === 'true') return;

      img.addEventListener('load', () => this.#ensureMobileGalleryImages(), { once: true });
      img.addEventListener('error', () => this.#repairImage(img), { once: true });
      img.dataset.whyshiftHeroBound = 'true';
    });
  }

  #initMobileGalleryRecovery() {
    if (!this.#isMobileViewport()) return;

    this.#bindLifecycleEvents();
    this.#observeMobileGallery();
    this.#bindMobileImageEvents();
    this.#initMobilePagination();
    this.#scheduleRepairPasses();
  }

  #getMobileTrack() {
    const track = this.querySelector('.whyshift-product-hero__mobile-track');
    return track instanceof HTMLElement ? track : null;
  }

  #getMobileDots() {
    return Array.from(this.querySelectorAll('.whyshift-product-hero__mobile-dot'));
  }

  #setActiveMobileDot(index) {
    const dots = this.#getMobileDots();
    if (!dots.length) return;

    const safeIndex = Math.max(0, Math.min(index, dots.length - 1));

    dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === safeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  #syncMobilePaginationFromScroll() {
    const track = this.#getMobileTrack();
    if (!track) return;

    const slide = track.querySelector('.whyshift-product-hero__mobile-slide');
    if (!(slide instanceof HTMLElement)) return;

    const slideWidth = slide.offsetWidth;
    if (!slideWidth) return;

    const index = Math.round(track.scrollLeft / slideWidth);
    this.#setActiveMobileDot(index);
  }

  #onMobileTrackScroll = () => {
    if (this.#mobilePaginationFrame) return;

    this.#mobilePaginationFrame = window.requestAnimationFrame(() => {
      this.#mobilePaginationFrame = undefined;
      this.#syncMobilePaginationFromScroll();
    });
  };

  #onMobileDotClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const dot = target.closest('.whyshift-product-hero__mobile-dot');
    if (!(dot instanceof HTMLButtonElement)) return;

    const index = Number.parseInt(dot.dataset.slideIndex || '', 10);
    if (Number.isNaN(index)) return;

    const track = this.#getMobileTrack();
    const slide = track?.querySelector('.whyshift-product-hero__mobile-slide');
    if (!(track instanceof HTMLElement) || !(slide instanceof HTMLElement)) return;

    const slideWidth = slide.offsetWidth;
    if (!slideWidth) return;

    track.scrollTo({ left: index * slideWidth, behavior: 'smooth' });
    this.#setActiveMobileDot(index);
  };

  #initMobilePagination() {
    const track = this.#getMobileTrack();
    const pagination = this.querySelector('.whyshift-product-hero__mobile-pagination');
    if (!track || !pagination) return;

    if (!this.#mobilePaginationBound) {
      track.addEventListener('scroll', this.#onMobileTrackScroll, { passive: true });
      pagination.addEventListener('click', this.#onMobileDotClick);
      this.#mobilePaginationBound = true;
    }

    this.#syncMobilePaginationFromScroll();
  }

  connectedCallback() {
    this.#sectionElement = this.closest('.shopify-section');
    this.#initMobileGalleryRecovery();
    this.#initMobilePagination();
    this.#initSellingPlans();
    this.#scheduleVariantPickerLayout();
    this.#observeVariantPicker();
    this.#bindVariantPickerChange();
    this.#bindDrawerTriggers();

    this.#sectionElement?.addEventListener('variant:update', this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    this.#variantPickerObserver?.disconnect();
    this.#mobileGalleryObserver?.disconnect();
    this.#clearRepairTimeouts();

    const track = this.#getMobileTrack();
    const pagination = this.querySelector('.whyshift-product-hero__mobile-pagination');

    if (track && this.#mobilePaginationBound) {
      track.removeEventListener('scroll', this.#onMobileTrackScroll);
    }

    if (pagination && this.#mobilePaginationBound) {
      pagination.removeEventListener('click', this.#onMobileDotClick);
    }

    if (this.#mobilePaginationFrame) {
      window.cancelAnimationFrame(this.#mobilePaginationFrame);
      this.#mobilePaginationFrame = undefined;
    }

    this.#mobilePaginationBound = false;

    if (this.#lifecycleBound) {
      window.removeEventListener('pageshow', this.#onPageShow);
      window.removeEventListener('pagereveal', this.#onPageReveal);
      this.#lifecycleBound = false;
    }

    this.#sectionElement?.removeEventListener('variant:update', this.#handleVariantUpdate);
    this.#sectionElement = null;
  }

  #getPriceContainer() {
    return this.querySelector('product-price [ref="priceContainer"]');
  }

  #getVariantPickerWrapper() {
    return this.querySelector('.whyshift-product-hero__variant-picker');
  }

  #getVariantHelpers() {
    const script = this.querySelector('[data-whyshift-variant-helpers]');
    if (!script?.textContent) return [];

    try {
      const helpers = JSON.parse(script.textContent);
      return Array.isArray(helpers) ? helpers : [];
    } catch {
      return [];
    }
  }

  #getHelperForOption(optionName, helpers) {
    const normalizedOption = this.#normalizeOptionName(optionName);

    return helpers.find((helper) =>
      (helper.options || []).some((option) => this.#normalizeOptionName(String(option)) === normalizedOption)
    );
  }

  #createHelperTrigger(helper) {
    if (helper.action === 'drawer' && helper.drawerId) {
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'whyshift-variant-option__helper';
      trigger.textContent = helper.label;
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.dataset.whyshiftDrawerOpen = helper.drawerId;
      return trigger;
    }

    if (helper.url) {
      const link = document.createElement('a');
      link.className = 'whyshift-variant-option__helper';
      link.href = helper.url;
      link.textContent = helper.label;
      return link;
    }

    return null;
  }

  #scheduleVariantPickerLayout() {
    requestAnimationFrame(() => {
      this.#initVariantPickerLayout();

      if (!this.querySelector('.whyshift-variant-option__header')) {
        window.setTimeout(() => this.#initVariantPickerLayout(), 100);
      }
    });
  }

  #observeVariantPicker() {
    const wrapper = this.#getVariantPickerWrapper();
    if (!wrapper || this.#variantPickerObserver) return;

    this.#variantPickerObserver = new MutationObserver(() => {
      this.#scheduleVariantPickerLayout();
    });

    this.#variantPickerObserver.observe(wrapper, {
      childList: true,
      subtree: true,
    });
  }

  #normalizeOptionName(name) {
    return name.trim().toLowerCase();
  }

  #extractOptionName(legend) {
    const labelText = Array.from(legend.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .map((node) => node.textContent.trim())
      .join(' ')
      .trim();

    if (labelText) return labelText;

    return legend.textContent.replace(/\s+/g, ' ').trim().split(':')[0].trim();
  }

  #bindVariantPickerChange() {
    if (this.dataset.variantPickerBound === 'true') return;

    this.#getVariantPickerWrapper()?.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;

      const fieldset = event.target.closest('fieldset.variant-option');
      if (fieldset) {
        this.#updateVariantLegend(fieldset);
      }
    });

    this.dataset.variantPickerBound = 'true';
  }

  #bindDrawerTriggers() {
    if (this.dataset.drawerTriggersBound === 'true') return;

    this.addEventListener('click', async (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-whyshift-drawer-open]') : null;
      if (!trigger || !this.contains(trigger)) return;

      event.preventDefault();
      const drawerId = trigger.dataset.whyshiftDrawerOpen;
      if (!drawerId) return;

      await customElements.whenDefined('whyshift-variant-drawer');
      const drawer = document.getElementById(drawerId);
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      }
    });

    this.dataset.drawerTriggersBound = 'true';
  }

  #initVariantPickerLayout() {
    const picker = this.#getVariantPickerWrapper()?.querySelector('variant-picker');
    if (!picker) return;

    const helpers = this.#getVariantHelpers();

    picker.querySelectorAll('fieldset.variant-option--buttons, fieldset.variant-option--swatches').forEach((fieldset) => {
      const existingLegend =
        fieldset.querySelector(':scope > legend') || fieldset.querySelector('.whyshift-variant-option__legend legend');
      if (!existingLegend) return;

      const optionName = fieldset.dataset.whyshiftOptionName || this.#extractOptionName(existingLegend);
      fieldset.dataset.whyshiftOptionName = optionName;

      const helper = this.#getHelperForOption(optionName, helpers);

      if (fieldset.dataset.whyshiftEnhanced === 'true') {
        const header = fieldset.querySelector('.whyshift-variant-option__header');
        const existingHelper = header?.querySelector('.whyshift-variant-option__helper');

        if (helper && header && !existingHelper) {
          const trigger = this.#createHelperTrigger(helper);
          if (trigger) header.appendChild(trigger);
        }

        this.#updateVariantLegend(fieldset);
        return;
      }

      const header = document.createElement('div');
      header.className = 'whyshift-variant-option__header';

      const legendWrap = document.createElement('div');
      legendWrap.className = 'whyshift-variant-option__legend';
      legendWrap.appendChild(existingLegend);
      header.appendChild(legendWrap);

      if (helper) {
        const trigger = this.#createHelperTrigger(helper);
        if (trigger) header.appendChild(trigger);
      }

      const buttonsWrap = document.createElement('div');
      buttonsWrap.className = fieldset.classList.contains('variant-option--swatches')
        ? 'whyshift-variant-option__buttons whyshift-variant-option__buttons--swatches'
        : 'whyshift-variant-option__buttons';

      fieldset.querySelectorAll(':scope > label').forEach((label) => {
        buttonsWrap.appendChild(label);
      });

      fieldset.prepend(header);
      fieldset.appendChild(buttonsWrap);
      fieldset.dataset.whyshiftEnhanced = 'true';

      this.#updateVariantLegend(fieldset);
    });
  }

  #updateVariantLegend(fieldset) {
    const legend = fieldset.querySelector('legend');
    const optionName = fieldset.dataset.whyshiftOptionName;
    if (!legend || !optionName) return;

    const checkedInput = fieldset.querySelector('input:checked');
    let selectedValue = '';

    if (checkedInput instanceof HTMLInputElement) {
      selectedValue =
        checkedInput.closest('label')?.querySelector('.variant-option__button-label__text')?.textContent?.trim() ||
        checkedInput.value;
    }

    legend.innerHTML = `<span class="whyshift-variant-option__name">${optionName.toUpperCase()}:</span><span class="whyshift-variant-option__value">${selectedValue.toUpperCase()}</span>`;
  }

  #initSellingPlans() {
    const form = this.querySelector('[data-whyshift-product-form]');
    const sellingPlanInput = form?.querySelector('[name="selling_plan"]');
    const planButtons = this.querySelectorAll('[data-whyshift-plan-button]');
    const noteEl = this.querySelector('[data-whyshift-plan-note]');

    if (!form || !planButtons.length) return;

    if (this.dataset.sellingPlansBound !== 'true') {
      planButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          const target = event.currentTarget;
          if (!(target instanceof HTMLButtonElement)) return;

          planButtons.forEach((item) => {
            item.classList.remove('whyshift-product-hero__plan--active');
            item.setAttribute('aria-pressed', 'false');
          });

          target.classList.add('whyshift-product-hero__plan--active');
          target.setAttribute('aria-pressed', 'true');

          this.#syncSellingPlan(target, sellingPlanInput, noteEl);
        });
      });

      this.dataset.sellingPlansBound = 'true';
    }

    const activeButton = this.querySelector('[data-whyshift-plan-button].whyshift-product-hero__plan--active');
    if (activeButton instanceof HTMLButtonElement) {
      this.#syncSellingPlan(activeButton, sellingPlanInput, noteEl);
    }
  }

  #syncSellingPlan(button, sellingPlanInput, noteEl) {
    const planId = button.dataset.whyshiftPlan || '';

    if (sellingPlanInput instanceof HTMLInputElement) {
      sellingPlanInput.value = planId;
    }

    this.#applyPlanPrice(button);

    if (noteEl) {
      noteEl.hidden = planId === '';
    }
  }

  #getSellingPlansByVariant() {
    const script = this.querySelector('[data-whyshift-selling-plans]');
    if (!script?.textContent) return {};

    try {
      const entries = JSON.parse(script.textContent);
      return entries.reduce((accumulator, entry) => {
        accumulator[String(entry.variantId)] = entry;
        return accumulator;
      }, {});
    } catch {
      return {};
    }
  }

  #updatePlanButtonPricing(variant) {
    const oneTimeButton = this.querySelector('[data-whyshift-plan-type="one-time"]');
    const subscribeButton = this.querySelector('[data-whyshift-plan-type="subscribe"]');
    const sellingPlansByVariant = this.#getSellingPlansByVariant();
    const subscribePricing = sellingPlansByVariant[String(variant.id)];

    if (oneTimeButton instanceof HTMLButtonElement) {
      const oneTimePrice = this.#formatMoney(variant.price);
      oneTimeButton.dataset.price = oneTimePrice;
      oneTimeButton.dataset.priceCents = String(variant.price);
      const priceEl = oneTimeButton.querySelector('[data-whyshift-plan-price]');
      if (priceEl) priceEl.textContent = oneTimePrice;

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        oneTimeButton.dataset.comparePrice = this.#formatMoney(variant.compare_at_price);
        oneTimeButton.dataset.comparePriceCents = String(variant.compare_at_price);
      } else {
        delete oneTimeButton.dataset.comparePrice;
        delete oneTimeButton.dataset.comparePriceCents;
      }
    }

    if (subscribeButton instanceof HTMLButtonElement && subscribePricing) {
      const subscribePrice = this.#formatMoney(subscribePricing.price);
      subscribeButton.dataset.price = subscribePrice;
      subscribeButton.dataset.priceCents = String(subscribePricing.price);
      const priceEl = subscribeButton.querySelector('[data-whyshift-plan-price]');
      if (priceEl) priceEl.textContent = subscribePrice;

      const oneTimeCents = subscribePricing.oneTimePrice ?? variant.price;
      if (oneTimeCents > subscribePricing.price) {
        subscribeButton.dataset.comparePrice = this.#formatMoney(oneTimeCents);
        subscribeButton.dataset.comparePriceCents = String(oneTimeCents);
      } else if (subscribePricing.compareAtPrice && subscribePricing.compareAtPrice > subscribePricing.price) {
        subscribeButton.dataset.comparePrice = this.#formatMoney(subscribePricing.compareAtPrice);
        subscribeButton.dataset.comparePriceCents = String(subscribePricing.compareAtPrice);
      } else {
        delete subscribeButton.dataset.comparePrice;
        delete subscribeButton.dataset.comparePriceCents;
      }
    }
  }

  #getPriceSettings() {
    return this.querySelector('[data-whyshift-price]');
  }

  #shouldShowOneTimeCompare() {
    const settings = this.#getPriceSettings();
    return settings?.dataset.showOneTimeCompare !== 'false';
  }

  #shouldShowSavingsBadge() {
    const settings = this.#getPriceSettings();
    return settings?.dataset.showSavingsBadge !== 'false';
  }

  #getComparePriceForPlan(button) {
    const planType = button.dataset.whyshiftPlanType;
    const oneTimeButton = this.querySelector('[data-whyshift-plan-type="one-time"]');

    if (planType === 'subscribe' && oneTimeButton instanceof HTMLButtonElement && this.#shouldShowOneTimeCompare()) {
      const subscribeCents = Number(button.dataset.priceCents || 0);
      const oneTimeCents = Number(oneTimeButton.dataset.priceCents || 0);

      if (oneTimeCents > subscribeCents && oneTimeButton.dataset.price) {
        return oneTimeButton.dataset.price;
      }
    }

    if (button.dataset.comparePrice) {
      return button.dataset.comparePrice;
    }

    return '';
  }

  #setRegularPriceDisplay(priceContainer, priceText) {
    const regularWrap = priceContainer.querySelector('.price__regular');
    const saleWrap = priceContainer.querySelector('.price__sale');

    saleWrap?.classList.add('price__hidden');

    if (regularWrap) {
      regularWrap.classList.remove('price__hidden');
      const priceEl = regularWrap.querySelector('.price');
      if (priceEl) priceEl.textContent = priceText;
      return;
    }

    const fallback = priceContainer.querySelector('.price-item--sale, .price-item--regular, .price');
    if (fallback) fallback.textContent = priceText;
  }

  #setSalePriceDisplay(priceContainer, priceText, compareText) {
    const regularWrap = priceContainer.querySelector('.price__regular');
    let saleWrap = priceContainer.querySelector('.price__sale');

    regularWrap?.classList.add('price__hidden');

    if (!saleWrap) {
      saleWrap = document.createElement('div');
      saleWrap.className = 'price__sale';
      priceContainer.appendChild(saleWrap);
    }

    saleWrap.classList.remove('price__hidden');
    saleWrap.innerHTML = `
      <span class="price-item__group">
        <span class="visually-hidden">Sale price&nbsp;</span>
        <span class="price-item--sale price"></span>
      </span>
      <span class="price-item__group">
        <span class="visually-hidden">Regular price&nbsp;</span>
        <span class="price-item--regular compare-at-price"></span>
      </span>
    `;

    const saleEl = saleWrap.querySelector('.price-item--sale, .price');
    const compareEl = saleWrap.querySelector('.compare-at-price, .price-item--compare');

    if (saleEl) saleEl.textContent = priceText;
    if (compareEl) {
      compareEl.textContent = compareText;
      compareEl.hidden = false;
    }
  }

  #updateSavingsBadge(button) {
    const badge = this.querySelector('[data-whyshift-savings-badge]');
    const settings = this.#getPriceSettings();
    if (!badge || !settings) return;

    if (!this.#shouldShowSavingsBadge() || button.dataset.whyshiftPlanType !== 'subscribe') {
      badge.hidden = true;
      badge.textContent = '';
      return;
    }

    const oneTimeButton = this.querySelector('[data-whyshift-plan-type="one-time"]');
    if (!(oneTimeButton instanceof HTMLButtonElement)) {
      badge.hidden = true;
      return;
    }

    const subscribeCents = Number(button.dataset.priceCents || 0);
    const oneTimeCents = Number(oneTimeButton.dataset.priceCents || 0);

    if (oneTimeCents <= subscribeCents) {
      badge.hidden = true;
      badge.textContent = '';
      return;
    }

    const savingsPercent = Math.round(((oneTimeCents - subscribeCents) / oneTimeCents) * 100);
    const label = settings.dataset.savingsLabel || 'SAVE';
    badge.textContent = `${label} ${savingsPercent}%`;
    badge.hidden = false;
  }

  #applyPlanPrice(button) {
    const priceContainer = this.#getPriceContainer();
    if (!priceContainer || !button.dataset.price) return;

    const compareText = this.#getComparePriceForPlan(button);

    if (compareText) {
      this.#setSalePriceDisplay(priceContainer, button.dataset.price, compareText);
    } else {
      this.#setRegularPriceDisplay(priceContainer, button.dataset.price);
    }

    this.#updateSavingsBadge(button);
  }

  #handleVariantUpdate = (event) => {
    const productId = this.querySelector('product-form-component')?.dataset.productId;
    if (!productId || event.detail?.data?.productId !== productId) return;

    const variant = event.detail.resource;
    if (!variant) return;

    this.#updateFeaturedMedia(variant);
    this.#scheduleVariantPickerLayout();
    this.#updatePlanButtonPricing(variant);
    this.#initMobilePagination();

    const activePlan = this.querySelector('[data-whyshift-plan-button].whyshift-product-hero__plan--active');
    if (activePlan instanceof HTMLButtonElement) {
      this.#applyPlanPrice(activePlan);
    }
  };

  #updateFeaturedMedia(variant) {
    const imageSrc = variant.featured_media?.preview_image?.src;
    if (!imageSrc) return;

    const srcsetWidths = [360, 640, 750, 828, 1080, 1280, 1600];
    const buildSrc = (width) => `${imageSrc}${imageSrc.includes('?') ? '&' : '?'}width=${width}`;

    this.querySelectorAll(
      '.whyshift-product-hero__media-primary img, .whyshift-product-hero__mobile-slide:first-child img'
    ).forEach((img) => {
      img.src = buildSrc(1600);
      img.srcset = srcsetWidths.map((width) => `${buildSrc(width)} ${width}w`).join(', ');
    });

    this.#bindMobileImageEvents();
    this.#scheduleRepairPasses();
  }

  #formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat);
    }

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(cents / 100);
  }
}

if (!customElements.get('whyshift-product-hero')) {
  customElements.define('whyshift-product-hero', WhyshiftProductHero);
}
